import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Container, Typography, Chip, MenuItem, Select, FormControl, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert, InputLabel, ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "../../api";
import { WebSocketContext } from "../context/websocketsContext";

/**
 * CommandCenter — a live, district-wide situational-awareness map (demo hero surface).
 *
 * Plots every incident that has a captured location as a colored pin (emergency / escalated /
 * open / closed), with live counts and a legend. It refreshes on a short POLL (reliable — a
 * new incident/emergency appears within seconds for ANY viewer, even one the backend WebSocket
 * fan-out doesn't target) AND listens on the WebSocket for instant updates when targeted. When a
 * NEW emergency arrives, the map flies to it and raises an unmissable banner.
 *
 * Map tiles: OpenStreetMap via Leaflet — no API key / billing required (matches the keyless
 * approach already used for the per-incident map).
 */

const POLL_MS = 5000;

// Location may arrive flat ({latitude,longitude}) or nested ({coords:{...}}); values may be
// numbers or Decimal-serialized strings. Coerce defensively (mirrors incidentLocationMap).
const toNum = (v) => {
  if (v === null || v === undefined || v === "") return NaN;
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
};
const coordsOf = (loc) => {
  if (!loc) return null;
  const src = loc.coords && (loc.coords.latitude !== undefined) ? loc.coords : loc;
  const lat = toNum(src.latitude);
  const lng = toNum(src.longitude);
  if (Number.isNaN(lat) || Number.isNaN(lng) || (lat === 0 && lng === 0)) return null;
  return [lat, lng];
};

const isEmergency = (i) => i.emergency === "True" || i.emergency === true;
const isEscalated = (i) => i.escalated === "True" || i.escalated === true;
const isClosed = (i) => (i.status || "").toLowerCase() === "closed";

const pinFor = (i) => {
  if (isEmergency(i)) return { color: "#DC484A", radius: 11, weight: 3 };      // red
  if (isEscalated(i)) return { color: "#FAAD14", radius: 9, weight: 2 };       // amber
  if (isClosed(i)) return { color: "#9E9E9E", radius: 6, weight: 1 };          // gray
  return { color: "#1890ff", radius: 8, weight: 2 };                           // blue (open)
};

// Imperatively fit/fly the map: fit to all pins on first load, fly to a new emergency after.
function MapController({ points, flyTo }) {
  const map = useMap();
  const didFit = useRef(false);
  useEffect(() => {
    if (!didFit.current && points.length > 0) {
      const lats = points.map((p) => p.pos[0]);
      const lngs = points.map((p) => p.pos[1]);
      map.fitBounds(
        [[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]],
        { padding: [50, 50], maxZoom: 15 }
      );
      didFit.current = true;
    }
  }, [points, map]);
  useEffect(() => {
    if (flyTo) map.flyTo(flyTo, 16, { duration: 1.5 });
  }, [flyTo, map]);
  return null;
}

const CommandCenter = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [subscribe, unsubscribe] = useContext(WebSocketContext);
  const [incidents, setIncidents] = useState([]);
  const [schoolFilter, setSchoolFilter] = useState("all");
  const [flyTo, setFlyTo] = useState(null);
  const [banner, setBanner] = useState(null);
  const knownEmergencies = useRef(new Set());
  const bannerTimer = useRef(null);

  // --- FEAT-055 active-alert broadcast (lockdown/threat/…) ---
  const ALERT_MESSAGES = {
    lockdown: "Lockdown in effect. Lock doors, stay quiet, and follow staff instructions.",
    threat: "Active threat reported. Follow staff instructions immediately.",
    shelter: "Shelter in place. Remain where you are until you receive an all-clear.",
    evacuate: "Evacuate now using the nearest safe exit. Follow staff instructions.",
    allclear: "All clear. The alert has ended. Resume normal activity.",
  };
  const [bcOpen, setBcOpen] = useState(false);
  const [bcType, setBcType] = useState("lockdown");
  const [bcSchool, setBcSchool] = useState("");
  const [bcMessage, setBcMessage] = useState(ALERT_MESSAGES.lockdown);
  const [bcSending, setBcSending] = useState(false);
  const [snack, setSnack] = useState(null);

  const openBroadcast = () => {
    setBcType("lockdown");
    setBcMessage(ALERT_MESSAGES.lockdown);
    setBcSchool(schoolFilter !== "all" ? schoolFilter : "");
    setBcOpen(true);
  };
  const chooseType = (t) => {
    if (!t) return;
    setBcType(t);
    setBcMessage(ALERT_MESSAGES[t] || "");
  };
  const sendBroadcast = async () => {
    if (!bcSchool || bcSending) return;
    setBcSending(true);
    try {
      const res = await api("post", { scope: "school", scopeId: bcSchool, alertType: bcType, message: bcMessage }, "broadcast");
      const body = res?.data || {};
      const n = body.recipients ?? 0;
      const schoolName = schools.find((s) => s.id === bcSchool)?.name || "the school";
      setSnack({ severity: "success", text: `${(body.title || bcType).toUpperCase()} alert sent to ${n} recipient${n === 1 ? "" : "s"} at ${schoolName}.` });
      setBcOpen(false);
    } catch (e) {
      setSnack({ severity: "error", text: "Could not send the alert. Please try again." });
    } finally {
      setBcSending(false);
    }
  };

  const fetchIncidents = async () => {
    try {
      const res = await api("get", { pageSize: 1000 }, "Incidents");
      const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setIncidents(list);
      // Detect NEW emergencies since the last refresh → fly + banner (the live "big bang").
      list.forEach((i) => {
        if (isEmergency(i) && !knownEmergencies.current.has(i.id)) {
          knownEmergencies.current.add(i.id);
          const pos = coordsOf(i.location);
          if (pos && knownEmergencies.current.size > 1) {
            // (size>1 so the initial load doesn't fly; only genuinely new ones after mount do)
            setFlyTo(pos);
            setBanner(`🚨 New emergency reported${i.schoolName ? ` at ${i.schoolName}` : ""}${i.number ? ` — incident #${i.number}` : ""}`);
            if (bannerTimer.current) clearTimeout(bannerTimer.current);
            bannerTimer.current = setTimeout(() => setBanner(null), 15000);
          }
        }
      });
    } catch (e) {
      /* keep current pins; next poll refills */
    }
  };

  useEffect(() => {
    // Seed known emergencies on mount without flying, then start polling.
    fetchIncidents();
    const t = setInterval(fetchIncidents, POLL_MS);
    return () => {
      clearInterval(t);
      if (bannerTimer.current) clearTimeout(bannerTimer.current);
    };
    // eslint-disable-next-line
  }, []);

  // Instant updates when the WebSocket frame IS targeted to this viewer (bonus on top of poll).
  useEffect(() => {
    const onFrame = () => fetchIncidents();
    subscribe("incidents", onFrame);
    subscribe("Emergency", onFrame);
    return () => {
      unsubscribe("incidents");
      unsubscribe("Emergency");
    };
    // eslint-disable-next-line
  }, [subscribe, unsubscribe]);

  const schools = useMemo(() => {
    const map = new Map();
    incidents.forEach((i) => { if (i.school && i.schoolName) map.set(i.school, i.schoolName); });
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [incidents]);

  const filtered = useMemo(
    () => (schoolFilter === "all" ? incidents : incidents.filter((i) => i.school === schoolFilter)),
    [incidents, schoolFilter]
  );

  const points = useMemo(
    () => filtered.map((i) => ({ i, pos: coordsOf(i.location) })).filter((p) => p.pos),
    [filtered]
  );

  const stats = useMemo(() => ({
    total: filtered.length,
    plotted: points.length,
    emergencies: filtered.filter(isEmergency).length,
    escalated: filtered.filter(isEscalated).length,
    open: filtered.filter((i) => !isClosed(i)).length,
  }), [filtered, points]);

  const style = {
    page: { display: "flex", flexDirection: "column", height: "calc(100vh - 60px)", padding: "0 !important", maxWidth: "none" },
    header: { display: "flex", alignItems: "center", gap: "16px", padding: "16px 24px", flexWrap: "wrap", borderBottom: `1px solid ${theme.palette.custom?.borderColor || "#eee"}` },
    title: { fontSize: "22px", fontWeight: 800, color: theme.palette.custom?.darkGray || "#262626", marginRight: "8px" },
    stat: { display: "flex", flexDirection: "column", alignItems: "center", minWidth: "72px" },
    statNum: { fontSize: "22px", fontWeight: 800, lineHeight: 1 },
    statLabel: { fontSize: "11px", color: theme.palette.text?.secondary || "#666", textTransform: "uppercase", letterSpacing: "0.5px" },
    mapWrap: { flex: 1, position: "relative" },
    banner: { position: "absolute", top: 16, left: "50%", transform: "translateX(-50%)", zIndex: 1000, background: "#DC484A", color: "#fff", padding: "12px 22px", borderRadius: "999px", fontWeight: 800, boxShadow: "0 4px 18px rgba(0,0,0,0.3)", fontSize: "15px" },
    legend: { position: "absolute", bottom: 20, right: 20, zIndex: 1000, background: "#fff", padding: "12px 14px", borderRadius: "8px", boxShadow: "0 2px 10px rgba(0,0,0,0.15)", display: "flex", flexDirection: "column", gap: "6px" },
    legendRow: { display: "flex", alignItems: "center", gap: "8px", fontSize: "12px" },
    dot: (c) => ({ width: 12, height: 12, borderRadius: "50%", background: c, display: "inline-block" }),
  };

  return (
    <Container sx={style.page}>
      <Box sx={style.header}>
        <Typography sx={style.title}>Command Center</Typography>
        <Box sx={style.stat}><Typography sx={[style.statNum, { color: "#262626" }]}>{stats.plotted}</Typography><Typography sx={style.statLabel}>On Map</Typography></Box>
        <Box sx={style.stat}><Typography sx={[style.statNum, { color: "#DC484A" }]}>{stats.emergencies}</Typography><Typography sx={style.statLabel}>Emergencies</Typography></Box>
        <Box sx={style.stat}><Typography sx={[style.statNum, { color: "#FAAD14" }]}>{stats.escalated}</Typography><Typography sx={style.statLabel}>Escalated</Typography></Box>
        <Box sx={style.stat}><Typography sx={[style.statNum, { color: "#1890ff" }]}>{stats.open}</Typography><Typography sx={style.statLabel}>Open</Typography></Box>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          onClick={openBroadcast}
          sx={{ background: "#DC484A", fontWeight: 800, "&:hover": { background: "#b8383a" } }}
        >
          🚨 Broadcast Alert
        </Button>
        <Chip label="● LIVE" sx={{ background: "#DC484A", color: "#fff", fontWeight: 800, letterSpacing: "1px" }} size="small" />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <Select value={schoolFilter} onChange={(e) => setSchoolFilter(e.target.value)} displayEmpty>
            <MenuItem value="all">All schools</MenuItem>
            {schools.map((s) => (<MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={style.mapWrap}>
        {banner ? <Box sx={style.banner}>{banner}</Box> : null}
        <MapContainer center={[38.82, -77.08]} zoom={12} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          <TileLayer
            attribution='&copy; OpenStreetMap contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapController points={points} flyTo={flyTo} />
          {points.map(({ i, pos }) => {
            const p = pinFor(i);
            return (
              <CircleMarker
                key={i.id}
                center={pos}
                radius={p.radius}
                pathOptions={{ color: p.color, fillColor: p.color, fillOpacity: 0.7, weight: p.weight }}
              >
                <Popup>
                  <div style={{ minWidth: 180 }}>
                    <div style={{ fontWeight: 800, marginBottom: 4 }}>
                      {isEmergency(i) ? "🚨 Emergency" : isEscalated(i) ? "⚠️ Escalated" : "Incident"}
                      {i.number ? ` #${i.number}` : ""}
                    </div>
                    {i.schoolName ? <div style={{ fontSize: 12 }}>{i.schoolName}</div> : null}
                    {i.type ? <div style={{ fontSize: 12 }}>Type: {i.type}</div> : null}
                    {i.status ? <div style={{ fontSize: 12 }}>Status: {i.status}</div> : null}
                    <div
                      style={{ marginTop: 8, color: "#1890ff", cursor: "pointer", fontWeight: 700, fontSize: 13 }}
                      onClick={() => navigate(`/incident/${i.id}`, { state: i })}
                    >
                      Open incident →
                    </div>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>

        <Box sx={style.legend}>
          <Box sx={style.legendRow}><span style={style.dot("#DC484A")} /> Emergency</Box>
          <Box sx={style.legendRow}><span style={style.dot("#FAAD14")} /> Escalated</Box>
          <Box sx={style.legendRow}><span style={style.dot("#1890ff")} /> Open</Box>
          <Box sx={style.legendRow}><span style={style.dot("#9E9E9E")} /> Closed</Box>
        </Box>
      </Box>

      <Dialog open={bcOpen} onClose={() => !bcSending && setBcOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: "#DC484A" }}>🚨 Broadcast an active alert</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: "#666", mb: 2 }}>
            This sends an unmissable alert to every student and staff member at the selected school
            (their device and app). Use only for a real safety situation.
          </Typography>
          <ToggleButtonGroup
            value={bcType}
            exclusive
            onChange={(e, t) => chooseType(t)}
            size="small"
            sx={{ flexWrap: "wrap", mb: 2 }}
          >
            <ToggleButton value="lockdown">Lockdown</ToggleButton>
            <ToggleButton value="threat">Active Threat</ToggleButton>
            <ToggleButton value="shelter">Shelter</ToggleButton>
            <ToggleButton value="evacuate">Evacuate</ToggleButton>
            <ToggleButton value="allclear">All Clear</ToggleButton>
          </ToggleButtonGroup>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="bc-school-label">School</InputLabel>
            <Select
              labelId="bc-school-label"
              label="School"
              value={bcSchool}
              onChange={(e) => setBcSchool(e.target.value)}
            >
              {schools.map((s) => (<MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Message"
            value={bcMessage}
            onChange={(e) => setBcMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBcOpen(false)} disabled={bcSending} color="inherit">Cancel</Button>
          <Button
            onClick={sendBroadcast}
            disabled={!bcSchool || bcSending}
            variant="contained"
            sx={{ background: "#DC484A", fontWeight: 800, "&:hover": { background: "#b8383a" } }}
          >
            {bcSending ? "Sending…" : "Send alert now"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={6000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)} variant="filled">{snack.text}</Alert> : null}
      </Snackbar>
    </Container>
  );
};

export default CommandCenter;
